import React, { ReactNode } from 'react';
import { graphql, useStaticQuery } from 'gatsby';
import Cookies from 'universal-cookie';
import { GlobalSettingsInterface } from '@/types';
import { capitalizeWords } from '@/lib/utils';

interface QueryResult {
  site: {
    siteMetadata: {
      siteUrl: string;
      title: string;
      description: string;
    };
  };
}

// Hook to fetch site metadata
const useSiteMetadata = (globalSettings: GlobalSettingsInterface[]) => {
  const cookies = new Cookies();
  const preferredLanguage = cookies.get('preferredLanguage') || 'en';

  const data: QueryResult = useStaticQuery(METADATA_QUERY);

  const filteredData = globalSettings.find((edge) => edge.node.language === preferredLanguage);

  const seoData = filteredData
    ? filteredData?.node?.seo
    : { title: '', description: '', image: { image: { asset: { url: '' } } } };

  const siteMetadata = data.site.siteMetadata;

  return {
    ...siteMetadata,
    title: seoData?.title || siteMetadata.title,
    description: seoData?.description || siteMetadata.description,
    image: seoData?.image?.image?.asset.url,
  };
};

// GraphQL query to get the data
const METADATA_QUERY = graphql`
  query {
    site {
      siteMetadata {
        siteUrl
        title
        description
      }
    }
  }
`;

interface LanguageVersion {
  language: string;
  href: string;
}

interface SEOProps {
  seoTitle?: string;
  title?: string;
  description?: string;
  image?: string;
  pathname?: string;
  children?: ReactNode;
  language: string;
  languageVersions: LanguageVersion[];
  globalSettings: GlobalSettingsInterface[];
}

const SEO: React.FC<SEOProps> = ({
  title,
  seoTitle,
  description,
  image,
  pathname,
  children,
  language,
  languageVersions,
  globalSettings,
}) => {
  const {
    title: defaultTitle,
    description: defaultDescription,
    siteUrl,
    image: defaultImage,
  } = useSiteMetadata(globalSettings);

  const seo = {
    title: seoTitle || (title ? `${capitalizeWords(title)} | ${defaultTitle}` : defaultTitle),
    description: description || defaultDescription,
    url: `${siteUrl}${pathname || ''}`,
    image: image || defaultImage,
  };

  return (
    <>
      <html lang={language} />
      <title>{seo.title}</title>
      <meta
        name='description'
        content={seo.description}
      />
      {seo.image && (
        <meta
          name='image'
          content={seo.image}
        />
      )}

      {/* Open Graph / Facebook */}
      <meta
        property='og:locale'
        content={language}
      />
      <meta
        property='og:site_name'
        content={defaultTitle}
      />
      <meta
        property='og:title'
        content={seo.title}
      />
      <meta
        property='og:description'
        content={seo.description}
      />
      <meta
        property='og:url'
        content={seo.url}
      />
      {seo.image && (
        <meta
          property='og:image'
          content={seo.image}
        />
      )}
      <meta
        property='og:type'
        content='website'
      />

      {/* Twitter */}
      <meta
        name='twitter:card'
        content='summary_large_image'
      />
      <meta
        name='twitter:title'
        content={seo.title}
      />
      <meta
        name='twitter:url'
        content={seo.url}
      />
      <meta
        name='twitter:description'
        content={seo.description}
      />
      {seo.image && (
        <meta
          name='twitter:image'
          content={seo.image}
        />
      )}

      {/* Add alternate language versions */}
      {languageVersions.map(({ language, href }) => {
        const sanitizedHref = href.replace('/homepage', '').replace(/\/?$/, '/');

        return (
          <link
            key={href}
            rel='alternate'
            href={`${siteUrl}${sanitizedHref}`}
            hrefLang={language}
          />
        );
      })}

      {children}
    </>
  );
};

export default SEO;
